/**
 * ComfortTool Engine 2.x — gebouw-lastmodel met scenario’s, optionele uurlijkse tak (roadmap NTA/BENG-richting).
 * Geen gecertificeerde NTA 8800 / VABI / energielabel-uitdraai — zie disclaimer.
 */
import { constructieTabelUSchaal, CONSTRUCTIE_TABEL_IDS, } from './constructionProxies.js';
import { hourlySolarGainWPerRoomSeries } from './solarHourlyGains.js';
import { envelopeUaWPerK, simulateHourly1R1CIdealLoads, simulateHourly1R1CUncoupledZones, simulateHourlyIdealLoads, simulateHourlyIdealLoadsUncoupledZones, thermalCapacityWhPerKFromVolume, } from './hourlyThermal.js';
export { simulateHourlyIdealLoads, hourlyQuasiSteadySelfCheck, simulateHourly1R1CIdealLoads, simulateHourly1R1CUncoupledZones, simulateHourlyIdealLoadsUncoupledZones, thermalCapacityWhPerKFromVolume, envelopeUaWPerK, } from './hourlyThermal.js';
export { runVabiReferenceChecks, VABI_REFERENCE_EXAMPLE_BANDS, VABI_SELF_CHECK_NOTE, } from './vabiReferenceChecks.js';
export { CONSTRUCTIE_TABEL_IDS, CONSTRUCTIE_TABEL_LABELS, CONSTRUCTIE_TABEL_LEGAL_NOTE, constructieTabelUSchaal, } from './constructionProxies.js';
export { hourlySolarGainWPerRoomSeries, verticalFacadeFactorFromOrientation } from './solarHourlyGains.js';
export const COMFORT_ENGINE_VERSION = '2.7.0';
export const ASSUMPTION_SET_ID = 'nl-multizone-solar-2026-04';
/** Transparantie voor UI/rapport — geen compliance-claim; wel wat de engine wél/niet doet. */
export const ENGINE_TRANSPARENCY_BULLETS = [
    'Piek-last: per ruimte transmissie (opaak + glas), ventilatie-proxy, zon op ramen (ontwerpdag); interne last personen.',
    'Opake schil: optionele constructie-proxy (schaal op U) + warmtebrug-toeslag — géén gekopieerde ISSO-tabellen.',
    'Jaar (graaddagen): schaling van piek met Open‑Meteo HDD/CDD t.o.v. NL-referentie.',
    'Jaar (uurlijks): Open‑Meteo T_buit per uur; optioneel GHI voor zon op glas per ruimte/oriëntatie; multi-zone = per ruimte zonder warmtewisseling tussen zones.',
    'Geen vocht/latent, geen inter-zone netwerk, geen officiële BENG/NTA-certificering.',
    'VABI-referentie: zelf testcases en bandbreedtes invoeren (runVabiReferenceChecks); geen automatische VABI-import.',
];
export const USER_SEGMENT_LABELS = {
    particulier: 'Particulier (woning)',
    installateur: 'Installateur / technisch adviseur',
    aannemer: 'Aannemer',
    vastgoedontwikkelaar: 'Vastgoedontwikkelaar',
    projectontwikkelaar: 'Projectontwikkelaar',
    bank_hypotheek: 'Bank / hypotheekadviseur',
    gemeente: 'Gemeente',
    woningcorporatie: 'Woningbouwcorporatie',
    vve: 'VvE / Meerjarenonderhoud',
};
const USER_SEGMENT_IDS = Object.keys(USER_SEGMENT_LABELS);
export const TYPE_WONING_LABELS = {
    Appartement: 'Appartement',
    Tussenwoning: 'Tussenwoning',
    Hoekwoning: 'Hoekwoning',
    Vrijstaand: 'Vrijstaand',
    Kantoor_pand: 'Kantoorpand',
    Winkel_of_horeca: 'Winkel / horeca',
    School_zorg_publiek: 'School, zorg of ander publiek gebouw',
    Bedrijfshal_werkplaats: 'Bedrijfshal / werkplaats',
    Gemengd_gebouw: 'Gemengd gebouw (woning + werk bijv.)',
};
const TYPE_WONING_IDS = Object.keys(TYPE_WONING_LABELS);
/** Alleen echte woningtypen (geen utiliteit). */
export function isResidentialTypeWoning(t) {
    return t === 'Appartement' || t === 'Tussenwoning' || t === 'Hoekwoning' || t === 'Vrijstaand';
}
export function isAppartementachtigGebouw(t) {
    return t === 'Appartement' || t === 'Gemengd_gebouw';
}
export const RUIMTE_POSITIE_LABELS = {
    buiten: 'Veel buitenwand (hoek / voorgevel)',
    half_buiten: 'Normaal (gemiddeld)',
    binnen: 'Kern (weinig buitenwand)',
};
const RUIMTE_POSITIE_IDS = ['buiten', 'half_buiten', 'binnen'];
const TYPE_GLAS_IDS = ['Enkel glas', 'Dubbel glas', 'HR++ glas', 'Triple glas', 'Onbekend'];
const ORIENTATIE_IDS = ['Zuid', 'West', 'Oost', 'Noord', 'Meerdere richtingen'];
const ZONWERING_IDS = ['Ja', 'Deels', 'Nee'];
const ISOLATIE_IDS = [
    'Geschat op bouwjaar',
    'Slecht',
    'Matig',
    'Goed',
    'Zeer goed',
];
const RENOVATIE_IDS = ['Geen', 'Basis', 'Diep'];
const LUCHT_IDS = ['Loos', 'Normaal', 'Strak'];
const METERKAST_IDS = [
    'weet_niet',
    'ruimte_genoeg',
    'waarschijnlijk_te_klein',
    'recent_vernieuwd',
];
const WATER_WP_IDS = ['weet_niet', 'ja_dichtbij', 'ver_weg', 'lastig'];
const BESTAANDE_IDS = [
    'weet_niet',
    'geen',
    'gas_cv',
    'stadswarmte',
    'elektrisch',
    'warmtepomp',
    'anders',
];
const PAND_IDS = ['normaal_bewoond', 'deels_leeg', 'heel_leeg', 'nieuwbouw'];
const KLUS_IDS = [
    'alleen_verwarmen_vernieuwen',
    'binnenwerk',
    'flinke_renovatie',
    'sloop_en_opbouw',
];
const WARMTE_VOORKEUR_IDS = [
    'geen_voorkeur',
    'liever_warmtepomp',
    'liever_gas',
    'liever_hybride',
    'liever_stadswarmte',
    'geen_gas_meer',
];
/** Referentie NL binnenland (engine-calibratie jaartotalen zonder postcode) */
export const NL_INTERIOR_DEGREE_DAY_REF = { hdd18: 2350, cdd24: 115 };
const METHODOLOGY_NOTE = 'Engine 2.6: per ruimte transmissie (opaak U·A + glas U·A), ventilatie (luchtverversing × volume) en zon op ramen (piek). Optioneel: uurlijk quasi-stationair 1-zone model (UA·ΔT per uur) voor jaartotalen uit een temperatuurreeks — zonder thermische massa, zonder vocht. Zelfde schil-proxy’s als voorheen; geen VABI/ISSO/NTA‑certificering of tekening-upload. Jaartotalen kunnen ook via Open‑Meteo graaddagen (bestaande schaling).';
const DISCLAIMER = 'Geen officiële NTA 8800-, ISSO-, VABI- of BENG-registratie. De uurlijkse tak is een uitbreidbare rekenkern, niet gevalideerd voor energielabelaangifte. Uitkomsten blijven indicatief; voor vergunning, subsidie, label of installatieontwerp een bevoegd adviseur en erkende software gebruiken.';
/** Woordenlijst voor rapport / UI (geen technische claims). */
export const JIP_EN_JANNEKE_LEGEND = [
    {
        titel: 'Warmtelast',
        tekst: 'Hoeveel warmte je moet bijstoken op de koudste reken-dag om het binnen op temperatuur te houden. Niet hetzelfde als je jaarfactuur.',
    },
    {
        titel: 'Koellast',
        tekst: 'Hoeveel “te veel” warmte je op een warme reken-dag kwijt moet (zon + warmte binnen + warmte die binnenkomt).',
    },
    {
        titel: 'Per kamer',
        tekst: 'Je vult zelf m² vloer, m² buitenwand, m² ramen en eventueel wand naar een koude ruimte in — zoals je van een tekening zou overnemen, maar zonder upload. De som van de kamers moet kloppen met je totaal woning.',
    },
    {
        titel: 'Niet hetzelfde als VABI',
        tekst: 'Gespecialiseerde bureaus werken vaak met VABI Elements en ISSO-publicaties. Wij geven vergelijkbare totalen en een kamer-verdeling in eenvoudige taal, met een lichtere rekentechniek — handig voor inschatting, niet voor een vergunningsdossier.',
    },
    {
        titel: 'Vergunning en regels in je gemeente',
        tekst: 'Soms heb je een omgevingsvergunning nodig voor ingrepen aan het huis of bij de buren in beeld. Wat precies telt, verschilt per gemeente en per jaar. Wij geven geen ja/nee op jouw adres — vraag het na bij de gemeente of je architect/adviseur.',
    },
    {
        titel: 'Subsidies (zoals ISDE) en “BRL”',
        tekst: 'Subsidies en uitvoeringsregels (zoals rond installateurschappen) veranderen vaak. Bedragen en voorwaarden hangen af van product, moment en soms je woning. Check altijd de actuele site van RVO of je installateur — gebruik de uitkomst van de scan alleen als eerste rekensom, niet als subsidie-aanvraag.',
    },
    {
        titel: 'Energielabel / EPC',
        tekst: 'Een officieel energielabel is iets anders dan deze last-scan. Voor verkoop, financiering of bepaalde regelingen heb je vaak een erkend adviseur nodig. Onze cijfers helpen om te praten over verwarming en koeling, maar ze vervangen geen labelrapport.',
    },
];
function normalizeSituatie(p) {
    const x = p ?? {};
    return {
        meterkast_status: METERKAST_IDS.includes(x.meterkast_status)
            ? x.meterkast_status
            : 'weet_niet',
        water_warmtepomp_dichtbij: WATER_WP_IDS.includes(x.water_warmtepomp_dichtbij)
            ? x.water_warmtepomp_dichtbij
            : 'weet_niet',
        bestaande_verwarming: BESTAANDE_IDS.includes(x.bestaande_verwarming)
            ? x.bestaande_verwarming
            : 'weet_niet',
        pand_gebruik: PAND_IDS.includes(x.pand_gebruik)
            ? x.pand_gebruik
            : 'normaal_bewoond',
        klus_omvang: KLUS_IDS.includes(x.klus_omvang)
            ? x.klus_omvang
            : 'alleen_verwarmen_vernieuwen',
    };
}
function normalizeWensen(p) {
    const x = p ?? {};
    return {
        warmte_voorkeur: WARMTE_VOORKEUR_IDS.includes(x.warmte_voorkeur)
            ? x.warmte_voorkeur
            : 'geen_voorkeur',
        wil_vloerverwarming_indien_kan: Boolean(x.wil_vloerverwarming_indien_kan),
        wil_geen_buitenunit_appartement: Boolean(x.wil_geen_buitenunit_appartement),
        wil_actieve_koeling: Boolean(x.wil_actieve_koeling),
    };
}
function normalizeRuimtesFromPartial(raw, vloeroppervlakte) {
    if (!raw || raw.length === 0) {
        if (vloeroppervlakte <= 0)
            return [];
        const o = Math.round(vloeroppervlakte * 100) / 100;
        return [
            {
                id: 'heel-woning',
                naam: 'Hele woning',
                vloeroppervlakte_m2: o,
                positie: 'half_buiten',
                verdieping: 1,
                buitenwand_m2: 0,
                ramen_m2: 0,
                wand_naar_ongverwarmd_m2: 0,
            },
        ];
    }
    return raw.map((r, i) => {
        const verdieping = typeof r.verdieping === 'number' && !Number.isNaN(r.verdieping) && r.verdieping >= 1
            ? Math.min(50, Math.floor(r.verdieping))
            : 1;
        const row = {
            id: (r.id ?? '').trim() || `ruimte-${i + 1}`,
            naam: (r.naam ?? `Ruimte ${i + 1}`).trim() || `Ruimte ${i + 1}`,
            vloeroppervlakte_m2: typeof r.vloeroppervlakte_m2 === 'number' && !Number.isNaN(r.vloeroppervlakte_m2)
                ? Math.round(Math.max(0, r.vloeroppervlakte_m2) * 100) / 100
                : 0,
            positie: RUIMTE_POSITIE_IDS.includes(r.positie)
                ? r.positie
                : 'half_buiten',
            verdieping,
            plafond_hoogte_m: typeof r.plafond_hoogte_m === 'number' && r.plafond_hoogte_m > 0 && !Number.isNaN(r.plafond_hoogte_m)
                ? Math.round(r.plafond_hoogte_m * 100) / 100
                : undefined,
            buitenwand_m2: typeof r.buitenwand_m2 === 'number' && !Number.isNaN(r.buitenwand_m2)
                ? Math.round(Math.max(0, r.buitenwand_m2) * 100) / 100
                : 0,
            ramen_m2: typeof r.ramen_m2 === 'number' && !Number.isNaN(r.ramen_m2)
                ? Math.round(Math.max(0, r.ramen_m2) * 100) / 100
                : 0,
            wand_naar_ongverwarmd_m2: typeof r.wand_naar_ongverwarmd_m2 === 'number' && !Number.isNaN(r.wand_naar_ongverwarmd_m2)
                ? Math.round(Math.max(0, r.wand_naar_ongverwarmd_m2) * 100) / 100
                : 0,
        };
        if (TYPE_GLAS_IDS.includes(r.type_glas))
            row.type_glas = r.type_glas;
        if (ORIENTATIE_IDS.includes(r.orientatie_ramen))
            row.orientatie_ramen = r.orientatie_ramen;
        if (ZONWERING_IDS.includes(r.zonwering))
            row.zonwering = r.zonwering;
        if (ISOLATIE_IDS.includes(r.isolatie_profiel))
            row.isolatie_profiel = r.isolatie_profiel;
        if (RENOVATIE_IDS.includes(r.renovatie_scenario))
            row.renovatie_scenario = r.renovatie_scenario;
        if (LUCHT_IDS.includes(r.luchtdichtheid))
            row.luchtdichtheid = r.luchtdichtheid;
        return row;
    });
}
function uEffFromBouwjaar(bouwjaar) {
    if (bouwjaar < 1965)
        return 1.35;
    if (bouwjaar < 1992)
        return 0.95;
    if (bouwjaar < 2012)
        return 0.58;
    return 0.34;
}
function isolatieProfielMultiplier(profiel) {
    switch (profiel) {
        case 'Slecht':
            return 1.18;
        case 'Matig':
            return 1.05;
        case 'Goed':
            return 0.9;
        case 'Zeer goed':
            return 0.78;
        case 'Geschat op bouwjaar':
        default:
            return 1.0;
    }
}
function renovatieMultiplier(scenario) {
    switch (scenario) {
        case 'Basis':
            return { u: 0.82, n: 0.92 };
        case 'Diep':
            return { u: 0.62, n: 0.78 };
        case 'Geen':
        default:
            return { u: 1, n: 1 };
    }
}
/** Opake U (W/m²K) vóór oppervlak — met constructie-proxy en warmtebrug op de opake schil. */
function roomOpaqueU_Wm2(input, r, multU) {
    const iso = r.isolatie_profiel ?? input.isolatie_profiel;
    const ren = r.renovatie_scenario ?? input.renovatie_scenario;
    const { u: ru } = renovatieMultiplier(ren);
    const uCore = uEffFromBouwjaar(input.bouwjaar) *
        isolatieProfielMultiplier(iso) *
        ru *
        multU *
        constructieTabelUSchaal(input.constructie_tabel);
    const br = 1 + input.warmtebrug_toeslag_frac;
    return Math.max(0.12, uCore * br);
}
function woningEnvelopeFactor(type) {
    switch (type) {
        case 'Vrijstaand':
            return 1.14;
        case 'Hoekwoning':
            return 1.06;
        case 'Tussenwoning':
            return 1.0;
        case 'Appartement':
            return 0.82;
        case 'Kantoor_pand':
            return 0.96;
        case 'Winkel_of_horeca':
            return 1.1;
        case 'School_zorg_publiek':
            return 1.04;
        case 'Bedrijfshal_werkplaats':
            return 1.18;
        case 'Gemengd_gebouw':
            return 1.02;
        default:
            return 1.0;
    }
}
function dakFactor(typeDak) {
    if (typeDak === 'Plat dak')
        return 1.04;
    return 1.0;
}
function achBaseFromBouwjaar(bouwjaar) {
    if (bouwjaar < 1992)
        return 0.52;
    if (bouwjaar < 2012)
        return 0.38;
    return 0.26;
}
function luchtdichtheidFactor(ld) {
    switch (ld) {
        case 'Loos':
            return 1.18;
        case 'Strak':
            return 0.88;
        case 'Normaal':
        default:
            return 1.0;
    }
}
function solarEquivalentWPerM2(orientatie) {
    switch (orientatie) {
        case 'Zuid':
            return 360;
        case 'West':
            return 250;
        case 'Oost':
            return 210;
        case 'Noord':
            return 110;
        case 'Meerdere richtingen':
        default:
            return 240;
    }
}
/** U-waarde glas (W/m²K) — orde-grootte t.o.v. glastype, voor transmissieterm op m² glas */
function uGlasWmk(typeGlas) {
    switch (typeGlas) {
        case 'Enkel glas':
            return 5.7;
        case 'Dubbel glas':
            return 2.8;
        case 'HR++ glas':
            return 1.15;
        case 'Triple glas':
            return 0.85;
        case 'Onbekend':
        default:
            return 2.5;
    }
}
function gGlas(typeGlas) {
    switch (typeGlas) {
        case 'Enkel glas':
            return 0.85;
        case 'Dubbel glas':
            return 0.62;
        case 'HR++ glas':
            return 0.48;
        case 'Triple glas':
            return 0.42;
        case 'Onbekend':
        default:
            return 0.58;
    }
}
function zonweringFactor(z) {
    switch (z) {
        case 'Ja':
            return 0.38;
        case 'Deels':
            return 0.62;
        case 'Nee':
        default:
            return 1.0;
    }
}
export function normalizeComfortInput(partial) {
    const y = new Date().getFullYear();
    const seg = partial.user_segment;
    const user_segment = seg && USER_SEGMENT_IDS.includes(seg) ? seg : 'particulier';
    const vloeroppervlakte = partial.vloeroppervlakte ?? 0;
    const ruimtes = normalizeRuimtesFromPartial(partial.ruimtes, vloeroppervlakte);
    return {
        vloeroppervlakte,
        plafondhoogte: partial.plafondhoogte ?? 0,
        bouwlagen: partial.bouwlagen ?? 1,
        bouwjaar: partial.bouwjaar ?? y,
        type_dak: partial.type_dak ?? 'Zadeldak',
        type_woning: TYPE_WONING_IDS.includes(partial.type_woning)
            ? partial.type_woning
            : 'Tussenwoning',
        type_glas: partial.type_glas ?? 'Dubbel glas',
        raamoppervlakte: partial.raamoppervlakte ?? 0,
        orientatie_ramen: partial.orientatie_ramen ?? 'Zuid',
        temperatuur: partial.temperatuur ?? 21,
        zonwering: partial.zonwering ?? 'Deels',
        personen: partial.personen ?? 2,
        isolatie_profiel: partial.isolatie_profiel ?? 'Geschat op bouwjaar',
        renovatie_scenario: partial.renovatie_scenario ?? 'Geen',
        luchtdichtheid: partial.luchtdichtheid ?? 'Normaal',
        postcode: normalizePostcodeVier(partial.postcode),
        user_segment,
        situatie: normalizeSituatie(partial.situatie),
        wensen: normalizeWensen(partial.wensen),
        ruimtes,
        constructie_tabel: CONSTRUCTIE_TABEL_IDS.includes(partial.constructie_tabel)
            ? partial.constructie_tabel
            : 'standaard_bouwjaar',
        warmtebrug_toeslag_frac: (() => {
            const x = partial.warmtebrug_toeslag_frac;
            if (typeof x !== 'number' || Number.isNaN(x))
                return 0;
            return Math.min(0.35, Math.max(0, x));
        })(),
    };
}
function normalizePostcodeVier(raw) {
    if (raw == null || raw === '')
        return '';
    const d = String(raw).replace(/\s/g, '').replace(/^NL/i, '');
    const four = d.replace(/\D/g, '').slice(0, 4);
    return four.length === 4 ? four : '';
}
export function validateComfortInput(input) {
    const i = normalizeComfortInput(input);
    const errs = [];
    if (i.vloeroppervlakte <= 0 || Number.isNaN(i.vloeroppervlakte)) {
        errs.push('Vloeroppervlakte is verplicht en moet groter dan 0 zijn.');
    }
    if (i.plafondhoogte <= 0 || Number.isNaN(i.plafondhoogte)) {
        errs.push('Plafondhoogte is verplicht en moet groter dan 0 zijn.');
    }
    if (i.bouwlagen <= 0 || Number.isNaN(i.bouwlagen)) {
        errs.push('Aantal bouwlagen moet minstens 1 zijn.');
    }
    if (i.bouwjaar < 1900 || i.bouwjaar > new Date().getFullYear() + 2) {
        errs.push('Kies een geldig bouwjaar.');
    }
    if (i.raamoppervlakte <= 0 || Number.isNaN(i.raamoppervlakte)) {
        errs.push('Raamoppervlakte is verplicht en moet groter dan 0 zijn.');
    }
    if (i.temperatuur <= 0 || Number.isNaN(i.temperatuur)) {
        errs.push('Binnen temperatuur moet groter dan 0 zijn.');
    }
    if (i.personen <= 0 || Number.isNaN(i.personen)) {
        errs.push('Aantal personen moet minstens 1 zijn (voor de warmte die mensen zelf afgeven).');
    }
    if (!/^\d{4}$/.test(i.postcode)) {
        errs.push('Postcode is verplicht: vul precies vier cijfers in (het stuk vóór de letters van je Nederlandse postcode).');
    }
    if (!TYPE_WONING_IDS.includes(i.type_woning)) {
        errs.push('Kies een geldig type gebouw.');
    }
    if (!USER_SEGMENT_IDS.includes(i.user_segment)) {
        errs.push('Kies een geldige doelgroep.');
    }
    if (!CONSTRUCTIE_TABEL_IDS.includes(i.constructie_tabel)) {
        errs.push('Kies een geldige constructie-proxy (tabelmodus).');
    }
    if (i.warmtebrug_toeslag_frac < 0 || i.warmtebrug_toeslag_frac > 0.35) {
        errs.push('Warmtebrug-toeslag moet tussen 0 en 0,35 (0–35%) liggen.');
    }
    if (i.ruimtes.length === 0) {
        errs.push('Er is minstens één ruimte nodig (of vul eerst het vloeroppervlak in).');
    }
    for (const r of i.ruimtes) {
        if (r.vloeroppervlakte_m2 <= 0 || Number.isNaN(r.vloeroppervlakte_m2)) {
            errs.push(`Ruimte "${r.naam}": vloeroppervlakte moet groter dan 0 zijn.`);
        }
    }
    const sumM2 = i.ruimtes.reduce((s, r) => s + r.vloeroppervlakte_m2, 0);
    const tol = Math.max(2, i.vloeroppervlakte * 0.12);
    if (i.vloeroppervlakte > 0 && Math.abs(sumM2 - i.vloeroppervlakte) > tol) {
        errs.push(`Som van kamers is ${sumM2.toFixed(1)} m²; totaal vloeroppervlak is ${i.vloeroppervlakte} m². Laat deze binnen circa 12% van elkaar vallen.`);
    }
    const sumRamenInvuld = i.ruimtes.reduce((s, r) => s + (r.ramen_m2 ?? 0), 0);
    if (sumRamenInvuld > 0.01 && i.raamoppervlakte > 0) {
        const t = Math.max(1, i.raamoppervlakte * 0.15);
        if (Math.abs(sumRamenInvuld - i.raamoppervlakte) > t) {
            errs.push(`Som van m² ramen in kamers (${sumRamenInvuld.toFixed(1)}) wijkt meer dan ~15% af van totaal raamoppervlak (${i.raamoppervlakte} m²). Pas aan of zet m² ramen per kamer op 0 voor automatische verdeling.`);
        }
    }
    return errs;
}
function envelopeAreaM2(opp, volume, fWoning, fDak) {
    const footprint = Math.max(opp, 1);
    const vol = Math.max(volume, 1);
    const heightProxy = vol / footprint;
    const wallApprox = 2 * Math.sqrt(Math.PI * footprint) * heightProxy * 0.35;
    return (1.1 * footprint * 2 + wallApprox) * fWoning * fDak;
}
export function segmentAudienceBriefForReport(segment) {
    switch (segment) {
        case 'particulier':
            return 'Lezer is een woningbezitter. Vermijd jargon; leg begrippen kort uit. Focus op comfort, jaarverbruik en vervolgstappen richting een installateur.';
        case 'installateur':
            return 'Lezer is installateur of technisch adviseur. Niveau: kW, scenario-bandbreedte, hydrauliek, geluid, COP-bandbreedte. Blijft indicatief — expliciet geen NTA-vervanger.';
        case 'aannemer':
            return 'Lezer is aannemer: planning, interfacing schil ↔ techniek, risico en bandbreedte t.o.v. offerte.';
        case 'vastgoedontwikkelaar':
        case 'projectontwikkelaar':
            return 'Lezer is project- of vastgoedontwikkelaar: sensitiviteit, typologie, haalbaarheid; geen woning-tutor.';
        case 'bank_hypotheek':
            return 'Lezer is bank/hypotheekadviseur: neutraal, memo-niveau, benadruk indicatief (geen substituut energielabel).';
        case 'gemeente':
            return 'Lezer is gemeente/beleid: referentie en uitleg, geen commerciële productpush.';
        case 'woningcorporatie':
            return 'Lezer is woningcorporatie: clusters, MJOP, standaardpakketten, onderhoudsperspectief.';
        case 'vve':
            return 'Lezer is VvE / MEH: besluitvorming, reserves, kostenverdeling, duidelijke voorbehouden.';
        default:
            return 'Professionele Nederlandse toon, helder en bondig.';
    }
}
function segmentChecklistSuggestions(segment, _result) {
    const common = [
        'Controleer kritische invoer (glas, raamoppervlakte, luchtdichtheid) tegen dossier of op locatie.',
        "Gebruik de drie scenario's als bandbreedte, niet als enkele waarheid.",
        'Vergunning bij de gemeente en subsidies (zoals ISDE): altijd de actuele site van je gemeente en RVO checken — regels wisselen per jaar en product.',
    ];
    const by = {
        particulier: [
            'Vraag een offerte bij een installateur en vermeld de pieklast (kW) en deze rapportage.',
            'Overweeg een blowerdoortest bij twijfel over luchtdichtheid.',
        ],
        installateur: [
            'Koppel aan hydraulische beeldvorming en geluidsruimte; buffer bij modulerende bron.',
            'Leg afgifte-NKW en elektrische aansluitwaarde vast in je offerte.',
        ],
        aannemer: [
            'Zet interfacing schil ↔ installatie vast in planning en scope.',
        ],
        vastgoedontwikkelaar: ['Benchmark met typologie in portfolio; gebruik scenario’s voor sensitiviteit.'],
        projectontwikkelaar: ['Reserveer bandbreedte voor herdimensionering na renovatie/transformatie.'],
        bank_hypotheek: ['Alleen ter ondersteuning dossier; geen formele energiedocumentatie.'],
        gemeente: ['Gebruik voor referentiegesprekken; klimaat is regio-proxy indien postcode is gebruikt.'],
        woningcorporatie: ['Vertaal naar standaardpakketten per bouwcluster en MJOP-cycli.'],
        vve: ['Neem mee in MEH-besluit; verdeel kosten volgens appartementenrecht.'],
    };
    return [...common, ...by[segment]];
}
function buildEngineInsights(input, base, scenarios, jaarW, jaarK, elekMin, elekMax, climateNote) {
    const opp = Math.max(input.vloeroppervlakte, 1);
    const jaarTot = Math.max(jaarW + jaarK, 1);
    const warmPct = Math.round((jaarW / jaarTot) * 100);
    const koelPct = Math.round((jaarK / jaarTot) * 100);
    const brutoW = base.qTransHeat + base.qVentHeat;
    const transPct = brutoW > 1 ? Math.round((base.qTransHeat / brutoW) * 100) : brutoW > 0 ? 50 : 0;
    const koelBruto = base.qTransCool + base.qVentCool + base.qInt + base.qSolCool;
    const zonPct = koelBruto > 1 ? Math.round((base.qSolCool / koelBruto) * 100) : 0;
    const basePeak = scenarios.find((s) => s.id === 'base')?.pieklastKW ?? 1;
    const peaks = scenarios.map((s) => s.pieklastKW);
    const minPeak = Math.min(...peaks);
    const maxPeak = Math.max(...peaks);
    const bandPct = basePeak > 0.05 ? Math.round(((maxPeak - minPeak) / basePeak) * 100) : 0;
    let onzekerheidLabel = 'normaal';
    if (bandPct < 20)
        onzekerheidLabel = 'smal';
    else if (bandPct > 38)
        onzekerheidLabel = 'breed';
    let interpretatieRegel = bandPct > 38
        ? "Ruime spreiding tussen scenario's — scherp bouwfysische parameters aan vóór definitieve capaciteit."
        : transPct >= 68
            ? 'Warmteverlies loopt vooral via de schil; isolatie en glas hebben grote hefboom.'
            : zonPct >= 32
                ? 'Koellast wordt sterk door zon op ramen gedreven; zonwering en glas zijn hefboompunten.'
                : "Balans tussen schil, ventilatie en zon — gebruik de scenario's als bandbreedte voor offertes.";
    if (climateNote) {
        interpretatieRegel = `${interpretatieRegel} ${climateNote}`;
    }
    return {
        warmteAandeelJaarPct: warmPct,
        koelAandeelJaarPct: koelPct,
        transmissieAandeelBrutoWarmtePct: transPct,
        zonAandeelKoelBrutoPct: zonPct,
        scenarioPiekBandPct: bandPct,
        onzekerheidLabel,
        energieIntensiteitKWhPerM2Jaar: Math.round(jaarTot / opp),
        elektraIntensiteitKWhPerM2JaarMin: Math.round(elekMin / opp),
        elektraIntensiteitKWhPerM2JaarMax: Math.round(elekMax / opp),
        interpretatieRegel,
    };
}
export function buildComfortReportPayload(input, result) {
    return {
        engineVersion: result.engineVersion,
        assumptionSetId: result.assumptionSetId,
        userSegment: input.user_segment,
        userSegmentLabel: USER_SEGMENT_LABELS[input.user_segment],
        audienceBrief: segmentAudienceBriefForReport(input.user_segment),
        segmentRegelgevingKort: result.segmentRegelgevingKort,
        checklist: segmentChecklistSuggestions(input.user_segment, result),
        input: {
            vloeroppervlakte: input.vloeroppervlakte,
            plafondhoogte: input.plafondhoogte,
            bouwlagen: input.bouwlagen,
            bouwjaar: input.bouwjaar,
            type_dak: input.type_dak,
            type_woning: input.type_woning,
            type_glas: input.type_glas,
            raamoppervlakte: input.raamoppervlakte,
            orientatie_ramen: input.orientatie_ramen,
            temperatuur: input.temperatuur,
            zonwering: input.zonwering,
            personen: input.personen,
            isolatie_profiel: input.isolatie_profiel,
            renovatie_scenario: input.renovatie_scenario,
            luchtdichtheid: input.luchtdichtheid,
            hasPostcode: Boolean(input.postcode),
            situatie: input.situatie,
            wensen: input.wensen,
            ruimtes: input.ruimtes,
        },
        resultSummary: {
            warmtelastKW: result.warmtelastKW,
            koellastKW: result.koellastKW,
            pieklastKW: result.pieklastKWDisplay,
            jaarverbruikKWh: result.jaarverbruik,
            jaarverbruikWarmteKWh: result.jaarverbruikWarmteKWh,
            jaarverbruikKoelKWh: result.jaarverbruikKoelKWh,
            scenarios: result.scenarios,
            designConditions: result.designConditions,
            systeem: result.systeem,
            alternatief: result.alternatief,
            jaarverbruikElektraHypotheseKWh: result.jaarverbruikElektraHypotheseKWh,
            engineInsights: result.engineInsights,
            climateContext: result.climateContext ?? null,
            lineItemsBase: result.lineItemsBase,
            methodologyNote: result.methodologyNote,
            disclaimer: result.disclaimer,
            ruimteLasten: result.ruimteLasten,
            jipEnJannekeSamenvatting: result.jipEnJannekeSamenvatting,
            gebouwIntensiteit: result.gebouwIntensiteit,
            adviesTechnieken: result.adviesTechnieken,
        },
    };
}
function systemAdvice(piekKW) {
    if (piekKW <= 3) {
        return {
            systeem: 'Lucht-lucht warmtepomp (split) + zone-regeling; optioneel infrarood bij zeer kleine last',
            alternatief: 'Hybride warmtepomp met bestaande afgifte of elektrische bijverwarming piek',
        };
    }
    if (piekKW <= 6) {
        return {
            systeem: 'Lucht-water warmtepomp met vloer/radiatoren + actieve koeling (split of HT-koeling)',
            alternatief: 'Multi-split LLWP + verbeterde schil (zonwering, glas)',
        };
    }
    if (piekKW <= 11) {
        return {
            systeem: 'Capaciteitsgestuurde lucht-water of VRF; vochtvraag en geluid expliciet in ontwerp',
            alternatief: 'Woning op knooppunt: buffer + modulerende warmtepomp',
        };
    }
    return {
        systeem: 'Maatwerk: hydraulische scheiding, mogelijk cascade of waterzijdig net — engineeringsstudie',
        alternatief: 'Faseer renovatie + verlaag piek vóór investering in centrale installatie',
    };
}
function roundKW(w) {
    return Math.round((w / 1000) * 10) / 10;
}
function lineItems(c) {
    return [
        {
            key: 'q_trans_w',
            label: 'Transmissie (ontwerp winter)',
            valueW: c.qTransHeat,
            category: 'transmissie',
            heatOrCool: 'warmte',
        },
        {
            key: 'q_vent_w',
            label: 'Ventilatie/infiltratie (luchtverversing proxy)',
            valueW: c.qVentHeat,
            category: 'ventilatie',
            heatOrCool: 'warmte',
        },
        {
            key: 'q_int_sub',
            label: 'Interne warmte (personen) — verlaagt verwarmingslast',
            valueW: -c.qInt,
            category: 'intern',
            heatOrCool: 'warmte',
        },
        {
            key: 'q_trans_c',
            label: 'Transmissie (ontwerp zomer)',
            valueW: c.qTransCool,
            category: 'transmissie',
            heatOrCool: 'koeling',
        },
        {
            key: 'q_vent_c',
            label: 'Ventilatie/infiltratie (zomer)',
            valueW: c.qVentCool,
            category: 'ventilatie',
            heatOrCool: 'koeling',
        },
        {
            key: 'q_int_c',
            label: 'Interne warmte (personen)',
            valueW: c.qInt,
            category: 'intern',
            heatOrCool: 'koeling',
        },
        {
            key: 'q_sol',
            label: 'Zonlast op ramen (piekproxy)',
            valueW: c.qSolCool,
            category: 'zon',
            heatOrCool: 'koeling',
        },
    ];
}
const WARMTE_POS_GEWICHT = {
    buiten: 1.14,
    half_buiten: 1,
    binnen: 0.86,
};
function resolveRoomGeometries(input) {
    const ruimtes = input.ruimtes;
    if (ruimtes.length === 0)
        return [];
    const sumFloor = ruimtes.reduce((s, r) => s + r.vloeroppervlakte_m2, 0);
    const opp = Math.max(input.vloeroppervlakte, sumFloor, 1e-6);
    let Vtot = ruimtes.reduce((s, r) => {
        const h = r.plafond_hoogte_m && r.plafond_hoogte_m > 0 ? r.plafond_hoogte_m : input.plafondhoogte;
        return s + r.vloeroppervlakte_m2 * h;
    }, 0);
    if (Vtot < 1e-3)
        Vtot = opp * input.plafondhoogte;
    const Aenv = envelopeAreaM2(opp, Vtot, woningEnvelopeFactor(input.type_woning), dakFactor(input.type_dak));
    const weights = ruimtes.map((r) => r.vloeroppervlakte_m2 * WARMTE_POS_GEWICHT[r.positie]);
    const floorShares = ruimtes.map((r) => r.vloeroppervlakte_m2);
    let Aw = ruimtes.map((r) => r.buitenwand_m2 ?? 0);
    let sumAw = Aw.reduce((a, b) => a + b, 0);
    let remW = Aenv - sumAw;
    if (remW > 0.001) {
        const zeroIdx = Aw.map((v, i) => (v < 0.001 ? i : -1)).filter((i) => i >= 0);
        const target = zeroIdx.length > 0 ? zeroIdx : ruimtes.map((_, i) => i);
        const wp = target.reduce((s, i) => s + weights[i], 0) || 1;
        for (const i of target) {
            Aw[i] += (remW * weights[i]) / wp;
        }
    }
    let Ag = ruimtes.map((r) => r.ramen_m2 ?? 0);
    let sumAg = Ag.reduce((a, b) => a + b, 0);
    let remG = input.raamoppervlakte - sumAg;
    if (remG > 0.001) {
        const zeroIdx = Ag.map((v, i) => (v < 0.001 ? i : -1)).filter((i) => i >= 0);
        const target = zeroIdx.length > 0 ? zeroIdx : ruimtes.map((_, i) => i);
        const fp = target.reduce((s, i) => s + floorShares[i], 0) || 1;
        for (const i of target) {
            Ag[i] += (remG * floorShares[i]) / fp;
        }
    }
    const sfin = Ag.reduce((a, b) => a + b, 0);
    if (sfin > 1e-6) {
        const f = input.raamoppervlakte / sfin;
        Ag = Ag.map((x) => Math.max(0, x * f));
    }
    return ruimtes.map((r, i) => {
        const h = r.plafond_hoogte_m && r.plafond_hoogte_m > 0 ? r.plafond_hoogte_m : input.plafondhoogte;
        const vloer = r.vloeroppervlakte_m2;
        const Ak = r.wand_naar_ongverwarmd_m2 ?? 0;
        return {
            id: r.id,
            naam: r.naam,
            positie: r.positie,
            vloer_m2: vloer,
            h_m: h,
            V_m3: vloer * h,
            A_buitenwand_m2: Math.max(0, Aw[i]),
            A_raam_m2: Math.max(0, Ag[i]),
            A_koude_m2: Math.max(0, Ak),
        };
    });
}
function buildRoomSolarPropsForHourly(input, geom) {
    return geom.map((g, i) => {
        const r = input.ruimtes[i];
        const glasT = r.type_glas ?? input.type_glas;
        return {
            A_raam_m2: g.A_raam_m2,
            orientatieLabel: r.orientatie_ramen ?? input.orientatie_ramen,
            gGlas: gGlas(glasT),
            zonweringFactor: zonweringFactor(r.zonwering ?? input.zonwering),
        };
    });
}
function jipRoomUitleg(naam, g, verdieping) {
    const Aop = g.A_buitenwand_m2 + g.A_koude_m2;
    const parts = [];
    const vLabel = verdieping <= 1 ? 'begane grond' : verdieping === 2 ? '1e verdieping' : `verdieping ${verdieping}`;
    parts.push(vLabel);
    if (Aop > 0.05)
        parts.push(`~${Math.round(Aop)} m² muur naar buiten of koude ruimte`);
    if (g.A_raam_m2 > 0.05)
        parts.push(`~${Math.round(g.A_raam_m2)} m² ramen`);
    if (parts.length === 1) {
        return `${naam}: ${vLabel} — geen m² wand/raam ingevuld; schatting uit totaal woning.`;
    }
    return `${naam}: ${parts.join(' · ')} — zoals je zelf zou intekenen, voor warmteverlies en zomerzon.`;
}
function computeScenarioFromRooms(input, geom, mult) {
    const T_i = input.temperatuur;
    const dTHeat = Math.max(0.1, T_i - -10);
    const dTCool = Math.max(0.1, 28 - T_i);
    const airK = 0.34;
    const sumFloor = geom.reduce((s, g) => s + g.vloer_m2, 0) || 1;
    const qIntTotal = input.personen * 80;
    let sumQtransHeat = 0;
    let sumQglassHeat = 0;
    let sumQventHeat = 0;
    let sumQtransCoolO = 0;
    let sumQglassCool = 0;
    let sumQventCool = 0;
    let sumQSol = 0;
    let sumWarm = 0;
    let sumCool = 0;
    const rowsDraft = [];
    for (let i = 0; i < geom.length; i++) {
        const g = geom[i];
        const r = input.ruimtes[i];
        const ren = r.renovatie_scenario ?? input.renovatie_scenario;
        const lucht = r.luchtdichtheid ?? input.luchtdichtheid;
        const { n: rn } = renovatieMultiplier(ren);
        const Uopaque = roomOpaqueU_Wm2(input, r, mult.u);
        const glasT = r.type_glas ?? input.type_glas;
        const Uglas = uGlasWmk(glasT);
        const nAch = achBaseFromBouwjaar(input.bouwjaar) * luchtdichtheidFactor(lucht) * rn * mult.n;
        const Aop = g.A_buitenwand_m2 + g.A_koude_m2;
        const qTrH = Uopaque * Aop * dTHeat;
        const qGlH = Uglas * g.A_raam_m2 * dTHeat;
        const qVeH = airK * nAch * g.V_m3 * dTHeat;
        const qIntR = qIntTotal * (g.vloer_m2 / sumFloor);
        const grossH = qTrH + qGlH + qVeH;
        const netH = Math.max(0, grossH - qIntR);
        const qTrC = Uopaque * Aop * dTCool;
        const qGlC = Uglas * g.A_raam_m2 * dTCool;
        const qVeC = airK * nAch * g.V_m3 * dTCool;
        const ori = r.orientatie_ramen ?? input.orientatie_ramen;
        const Ieq = solarEquivalentWPerM2(ori);
        const qSolR = g.A_raam_m2 *
            Ieq *
            gGlas(glasT) *
            zonweringFactor(r.zonwering ?? input.zonwering) *
            mult.sol;
        const cool = qTrC + qGlC + qVeC + qIntR + qSolR;
        sumQtransHeat += qTrH;
        sumQglassHeat += qGlH;
        sumQventHeat += qVeH;
        sumQtransCoolO += qTrC;
        sumQglassCool += qGlC;
        sumQventCool += qVeC;
        sumQSol += qSolR;
        sumWarm += netH;
        sumCool += cool;
        const m2 = Math.max(g.vloer_m2, 1e-6);
        const v = r.verdieping ?? 1;
        rowsDraft.push({
            id: g.id,
            naam: g.naam,
            vloeroppervlakte_m2: g.vloer_m2,
            verdieping: v,
            positie: g.positie,
            warmte_W: Math.round(netH),
            koel_W: Math.round(cool),
            piek_W: Math.round(Math.max(netH, cool)),
            warmte_W_m2: Math.round(netH / m2),
            koel_W_m2: Math.round(cool / m2),
            warmte_transmissie_W: Math.round(qTrH + qGlH),
            warmte_ventilatie_W: Math.round(qVeH),
            koel_transmissie_W: Math.round(qTrC + qGlC),
            koel_ventilatie_W: Math.round(qVeC),
            koel_zon_W: Math.round(qSolR),
            uitleg_regel: jipRoomUitleg(g.naam, g, v),
        });
    }
    const core = {
        qTransHeat: sumQtransHeat + sumQglassHeat,
        qVentHeat: sumQventHeat,
        qInt: qIntTotal,
        qTransCool: sumQtransCoolO + sumQglassCool,
        qVentCool: sumQventCool,
        qSolCool: sumQSol,
        warmteW: sumWarm,
        koelW: sumCool,
    };
    return { warmteW: sumWarm, koelW: sumCool, core, rowsDraft };
}
export function computePerZoneHourlyLinearCoeffs(input) {
    const ni = normalizeComfortInput(input);
    const geom = resolveRoomGeometries(ni);
    const T_i = ni.temperatuur;
    const dTHeat = Math.max(0.1, T_i - -10);
    const dTCool = Math.max(0.1, 28 - T_i);
    const airK = 0.34;
    const sumFloor = geom.reduce((s, g) => s + g.vloer_m2, 0) || 1;
    const qIntTotal = ni.personen * 80;
    const multU = 1;
    const out = [];
    for (let i = 0; i < geom.length; i++) {
        const g = geom[i];
        const r = ni.ruimtes[i];
        const ren = r.renovatie_scenario ?? ni.renovatie_scenario;
        const lucht = r.luchtdichtheid ?? ni.luchtdichtheid;
        const { n: rn } = renovatieMultiplier(ren);
        const Uopaque = roomOpaqueU_Wm2(ni, r, multU);
        const glasT = r.type_glas ?? ni.type_glas;
        const Uglas = uGlasWmk(glasT);
        const nAch = achBaseFromBouwjaar(ni.bouwjaar) * luchtdichtheidFactor(lucht) * rn;
        const Aop = g.A_buitenwand_m2 + g.A_koude_m2;
        const qTrH = Uopaque * Aop * dTHeat;
        const qGlH = Uglas * g.A_raam_m2 * dTHeat;
        const qVeH = airK * nAch * g.V_m3 * dTHeat;
        const qTrC = Uopaque * Aop * dTCool;
        const qGlC = Uglas * g.A_raam_m2 * dTCool;
        const qVeC = airK * nAch * g.V_m3 * dTCool;
        const qIntR = qIntTotal * (g.vloer_m2 / sumFloor);
        out.push({
            id: g.id,
            naam: g.naam,
            uaHeat_WperK: (qTrH + qGlH + qVeH) / dTHeat,
            uaCool_WperK: (qTrC + qGlC + qVeC) / dTCool,
            internalGains_W: qIntR,
            volume_m3: g.V_m3,
        });
    }
    return out;
}
function finalizeRoomJaar(rows, jaarWarm, jaarKoel, sumWarm, sumCool) {
    const sW = sumWarm > 1 ? sumWarm : 1;
    const sC = sumCool > 1 ? sumCool : 1;
    return rows.map((r) => ({
        ...r,
        jaar_warmte_kWh: Math.round(jaarWarm * (r.warmte_W / sW)),
        jaar_koel_kWh: Math.round(jaarKoel * (r.koel_W / sC)),
    }));
}
function jipEnJannekeBureauSamenvatting(nR) {
    if (nR <= 0) {
        return 'Geen ruimtes — vul eerst je woning in.';
    }
    if (nR === 1) {
        return 'Eén vlak voor de hele woning: m² buitenwand en m² ramen mogen op 0 — dan schatten we de schil uit je totaaloppervlak. Voor strakkere uitkomsten (zoals bij een bureau-invulbrief): splits kamers en vul per ruimte m² wand en m² ramen handmatig in — zonder plattegrond-upload.';
    }
    return `Per ruimte rekenen we warmteverlies door muren en ramen, lucht die te vaak wisselt, en zomerzon op je ramen. Je kunt per kamer je eigen plafondhoogte, verdieping, glas, raamkant, zonwering en hoe strak het huis is invullen — anders pakt de scan de standaard voor het hele huis. De som van de kamers moet ongeveer kloppen met je totaal woning.`;
}
/** Korte uitleg welke soort regels vaak spelen — afhankelijk van wie de scan invult (geen juridisch advies). */
export function segmentRegelgevingKortText(segment) {
    switch (segment) {
        case 'particulier':
            return 'Voor particulieren spelen vaak de gemeente (soms een vergunning), geluid en plek van een buitenunit, subsidies op warmtepompen (regels wisselen), en een apart energielabel als je verkoopt. Alles hangt af van jaar en product — vraag na bij gemeente, RVO en je installateur.';
        case 'installateur':
            return 'Voor installateurs: houd rekening met actuele uitvoeringsregels (bijv. rond gas en veiligheid), vergunningen bij klanten, subsidievoorwaarden en het verschil tussen dit rekentooltje en een officieel label of NTA-dossier.';
        case 'aannemer':
            return 'Bij bouw en verbouw: planning, vergunningen bij ingrepen aan de schil, en afstemming met de installateur. Regels en subsidies verschillen per project en jaar.';
        case 'vastgoedontwikkelaar':
        case 'projectontwikkelaar':
            return 'Bij projecten: vergunningen, BENG/energie-eisen voor nieuwbouw, warmtenet versus individueel — per locatie en jaar anders. Gebruik dit als eerste inschatting, niet als verkoopdocument.';
        case 'bank_hypotheek':
            return 'Voor financiers: dit is geen officieel energielabel of NTA-uitdraai; wel een gespreksondersteuner over verwarmings- en koelcapaciteit.';
        case 'gemeente':
            return 'Voor gemeenten: referentie voor gesprekken met inwoners; vergunningen en lokale beleidsregels blijven leidend en wisselen per tijd.';
        case 'woningcorporatie':
            return 'Voor corporaties: clusters, MJOP en vervangingsmomenten; subsidies en verhuurpunten wisselen — dit tooltje geeft geen contractuele basis.';
        case 'vve':
            return 'Voor VvE’s: besluitvorming, geluid en plek van gezamenlijke techniek; regels per appartementencomplex en jaar verschillen sterk.';
        default:
            return 'Regels, subsidies en vergunningen hangen af van jaar, regio en product — altijd actueel laten bevestigen door een adviseur.';
    }
}
function buildOfficieelAdviesRegels(_input) {
    const jaar = new Date().getFullYear();
    return [
        {
            categorie: 'officieel',
            titel: 'Vergunning bij de gemeente',
            toelichting: 'Sommige klussen aan het huis (uitbouw, dakkapel, soms een grote warmtepomp buiten) kunnen een omgevingsvergunning nodig hebben. Elke gemeente werkt net anders. Vraag het na vóór je start — wij geven geen ja/nee op jouw adres.',
        },
        {
            categorie: 'officieel',
            titel: `Subsidies en regels (${jaar})`,
            toelichting: 'Regelingen zoals ISDE en soms “SEH”-achtige routes veranderen; bedragen en voorwaarden staan op de site van RVO of bij je installateur. BRL-100 gaat over veilig werken met gasinstallaties — relevant als je (nog) gas gebruikt. Check altijd de actuele tekst, niet alleen dit rapport.',
        },
        {
            categorie: 'officieel',
            titel: 'Energielabel / EPC',
            toelichting: 'Een officieel label voor verkoop of financiering is iets anders dan deze scan. Daarvoor heb je meestal een erkend adviseur nodig. Onze kW-getallen helpen om te praten over verwarming en koeling.',
        },
    ];
}
function buildSituatieInstallatieAdvies(input, piekKW) {
    const s = input.situatie;
    const out = [];
    if (s.meterkast_status === 'waarschijnlijk_te_klein') {
        out.push({
            categorie: 'installatie',
            titel: 'Meterkast eerst laten bekijken',
            toelichting: 'Je gaf aan dat je meterkast misschien te klein is. Een warmtepomp of fornuis op stroom vraagt vaak dikkere groepen. Laat een erkende elektricien kijken vóór je een systeem kiest — anders kan de offerte achteraf wijzigen.',
        });
    }
    else if (s.meterkast_status === 'recent_vernieuwd') {
        out.push({
            categorie: 'installatie',
            titel: 'Meterkast recent vernieuwd',
            toelichting: 'Dat scheelt vaak richting een warmtepomp: er is dan meestal ruimte voor extra zekeringen. Laat toch even bevestigen hoeveel vrij vermogen je nog hebt (ook voor koken of een auto-lader).',
        });
    }
    if (s.water_warmtepomp_dichtbij === 'ver_weg' || s.water_warmtepomp_dichtbij === 'lastig') {
        out.push({
            categorie: 'installatie',
            titel: 'Water ver van de warmtepomp',
            toelichting: 'Als er geen waterkraan of leiding dichtbij de plek van de buitenunit is, worden leidingen langer (meer werk, vorst denken). Zet dit op je offerte-verlanglijst.',
        });
    }
    if (s.bestaande_verwarming === 'gas_cv') {
        out.push({
            categorie: 'installatie',
            titel: 'Je hebt nu gas',
            toelichting: 'Hybride (gas + warmtepomp) kan minder ingreep aan leidingen en radiatoren betekenen. “Alles elektrisch” is vaak wel meer werk aan de meterkast. Laat beide opties uitrekenen.',
        });
    }
    else if (s.bestaande_verwarming === 'stadswarmte') {
        out.push({
            categorie: 'installatie',
            titel: 'Stadsverwarming',
            toelichting: 'Je zit al op warmtenet: wijzigingen gaan via de leverancier en huisaansluiting. Onze piek-kW helpt om te vergelijken met andere scenario’s, maar het contract bepaalt veel.',
        });
    }
    else if (s.bestaande_verwarming === 'geen') {
        out.push({
            categorie: 'installatie',
            titel: 'Nog geen vaste verwarming',
            toelichting: 'Bij een leeg pand of nieuwbouw kun je vaak vrijer kiezen: vloer, lucht of radiatoren. Plan wel meteen de meterkast en (indien nodig) water mee.',
        });
    }
    if (s.pand_gebruik === 'heel_leeg' || s.pand_gebruik === 'nieuwbouw') {
        out.push({
            categorie: 'installatie',
            titel: 'Leeg of nieuwbouw',
            toelichting: 'Ideaal moment om afgifte (bijv. vloer) en leidingen mee te trekken vóór de vloer dicht is. Houd rekening met stof, vocht en planning — niet alles in één week klaar.',
        });
    }
    if (s.klus_omvang === 'flinke_renovatie' || s.klus_omvang === 'sloop_en_opbouw') {
        out.push({
            categorie: 'installatie',
            titel: 'Grote verbouw',
            toelichting: 'Bij sloop of grote renovatie: koppel verwarming aan de volgorde van de bouw (dak en ramen eerst = rustigere lasten). Dimensionering kan na isolatie lager uitvallen — herhaal deze scan als de schil klaar is.',
        });
    }
    if (piekKW >= 9 && s.meterkast_status === 'weet_niet') {
        out.push({
            categorie: 'installatie',
            titel: 'Hoge piek — stroom checken',
            toelichting: 'Je piek is fors. Vraag expliciet naar de maximale aansluiting (Ampère) en of de netbeheerder iets moet verzwaren.',
        });
    }
    return out;
}
function buildWensenAdvies(input, piekKW, maxKamerWm2, koelHoog) {
    const w = input.wensen;
    const out = [];
    if (w.warmte_voorkeur === 'liever_warmtepomp') {
        out.push({
            categorie: 'warmtebron',
            titel: 'Jouw voorkeur: warmtepomp',
            toelichting: 'We nemen dat mee: lucht-water of lucht-lucht past vaak bij moderne isolatie. Als je radiatoren nog heet water willen, kan hybride tijdelijk handig zijn.',
        });
    }
    if (w.warmte_voorkeur === 'liever_gas') {
        out.push({
            categorie: 'warmtebron',
            titel: 'Jouw voorkeur: gas',
            toelichting: 'Nieuwe gasketels worden zeldzamer; kijk of je gemeente of regels iets voorschrijven. Voor bestaand kan gas nog, maar op termijn wil je vaak minder afhankelijk van gas worden.',
        });
    }
    if (w.warmte_voorkeur === 'geen_gas_meer') {
        out.push({
            categorie: 'warmtebron',
            titel: 'Liever geen gas meer',
            toelichting: 'Dan richting elektrische warmtepomp of warmtenet. Let op meterkast en piekvermogen; vraag een offerte die expliciet “zonder gas” kan.',
        });
    }
    if (w.warmte_voorkeur === 'liever_hybride') {
        out.push({
            categorie: 'combinatie',
            titel: 'Jouw voorkeur: hybride',
            toelichting: 'Hybride kan pieken opvangen met gas en de rest met een pomp — handig als je radiatoren nog warm water vragen. Op termijn kun je vaak verder ver elektrisch.',
        });
    }
    if (w.warmte_voorkeur === 'liever_stadswarmte') {
        out.push({
            categorie: 'combinatie',
            titel: 'Jouw voorkeur: warmtenet',
            toelichting: 'Alleen haalbaar als er een leiding voor het huis ligt. Anders is individuele warmtepomp realistischer.',
        });
    }
    if (w.wil_geen_buitenunit_appartement && isAppartementachtigGebouw(input.type_woning)) {
        out.push({
            categorie: 'overig',
            titel: 'Geen buitenunit gewenst (appartement)',
            toelichting: 'Dan zijn oplossingen met binnen-unit (lucht-lucht met leidingwerk) of collectieve techniek interessanter. Waterzijdige warmtepomp zonder buitenunit is zeldzaam — bespreek opties met een installateur die appartementen kent.',
        });
    }
    if (w.wil_vloerverwarming_indien_kan) {
        if (maxKamerWm2 >= 90) {
            out.push({
                categorie: 'afgifte',
                titel: 'Vloerverwarming: let op zware kamers',
                toelichting: 'Je wilt graag vloerverwarming; één of meer kamers vragen veel vermogen per m². Vaak combineer je vloer met een convector of radiator voor piekdagen.',
            });
        }
        else {
            out.push({
                categorie: 'afgifte',
                titel: 'Vloerverwarming past vaak',
                toelichting: 'Je last per m² is niet extreem hoog: vloer of wand op lage temperatuur sluit vaak goed aan bij een warmtepomp.',
            });
        }
    }
    if (w.wil_actieve_koeling) {
        out.push({
            categorie: 'koeling',
            titel: 'Actieve koeling gewenst',
            toelichting: koelHoog
                ? 'Bij jouw koellast of zon op ramen is actieve koeling (bijv. split of koud water) logisch naast zonwering en goede ramen.'
                : 'Je koellast is beperkt: eerst zonwering en nachtluchten proberen; koeling kan later alsnog.',
        });
    }
    if (w.warmte_voorkeur === 'liever_warmtepomp' && input.situatie.meterkast_status === 'waarschijnlijk_te_klein') {
        out.push({
            categorie: 'overig',
            titel: 'Let op: meterkast en warmtepomp',
            toelichting: 'Je wilt een warmtepomp, maar je meterkast lijkt krap. Dat is geen “nee”, wel “eerst elektricien”. Soms is hybride tijdelijk zachter voor de aansluiting.',
        });
    }
    if (w.warmte_voorkeur === 'liever_gas' && piekKW > 15) {
        out.push({
            categorie: 'overig',
            titel: 'Grote woning op gas',
            toelichting: 'Bij hoge piek op gas worden ketels groot en de ruimte voor rookgas en onderhoud belangrijk. Vergelijk met (hybride) warmtepomp voor comfort en toekomst.',
        });
    }
    return out;
}
export function buildTechniekAdvies(input, piekKW, wm2, km2, maxKamerWarmteWm2, koelHoog) {
    const out = [];
    out.push({
        categorie: 'overig',
        titel: 'Leeswijzer',
        toelichting: 'Onderstaande opties zijn samengesteld op basis van je berekende pieklast en intensiteit. Kies altijd afgifte en bron samen; laat dimensionering bevestigen door een installateur.',
    });
    if (piekKW <= 3.5) {
        out.push({
            categorie: 'warmtebron',
            titel: 'Lucht-lucht warmtepomp (split of multi-split)',
            toelichting: 'Kleine piek: geschikt voor directe elektrische warmtepomp naar de ruimte. Let op geluid naar buren bij buitenunit (vooral appartement).',
        });
        out.push({
            categorie: 'afgifte',
            titel: 'Afgifte: directe lucht of kleine lage-temp vloerzone',
            toelichting: 'Bij zeer lage W/m² op de zwaarste kamer kan vloerverwarming op lage aanvoer; anders luchtafgifte of compacte convector.',
        });
    }
    else if (piekKW <= 7) {
        out.push({
            categorie: 'warmtebron',
            titel: 'Lucht-water warmtepomp',
            toelichting: 'Typisch voor midden-piek: buffervat, modulerende bron, hydraulische balans. Combineer met bestaande radiatoren alleen als aanvoertemperatuur past of na verzwaren afgifte.',
        });
        out.push({
            categorie: 'combinatie',
            titel: 'Hybride ketel + warmtepomp',
            toelichting: 'Als bestaande radiatoren hoge aanvoer nodig hebben: tijdelijk gas/top voor piek, WP voor basislast — minder ingreep aan afgifte.',
        });
    }
    else {
        out.push({
            categorie: 'warmtebron',
            titel: 'Krachtige lucht-water of bodem/water',
            toelichting: 'Hoge piek: buffer, mogelijk cascade of tweestaps, elektrische aansluiting en fasering expliciet uitwerken.',
        });
        out.push({
            categorie: 'afgifte',
            titel: 'Afgifte mix',
            toelichting: 'Overweeg vloer/wand op lagere temperatuur gecombineerd met staande radiatoren voor piekdagen; hydraulisch ontkoppelen waar nodig.',
        });
    }
    if (maxKamerWarmteWm2 >= 95) {
        out.push({
            categorie: 'afgifte',
            titel: 'Zware kamer(s): hogere afgifte-capaciteit',
            toelichting: 'Een of meer ruimtes vragen veel W/m² vloer: check convectoren of ventil-convector; pure vloer alleen als aanvoer laag genoeg is.',
        });
    }
    else if (maxKamerWarmteWm2 <= 55 && piekKW <= 8) {
        out.push({
            categorie: 'afgifte',
            titel: 'Lage-temperatuur-afgifte geschikt',
            toelichting: 'Relatief lage W/m² op kamers: vloer- of wandverwarming sluit vaak goed aan bij een moderne warmtepomp.',
        });
    }
    if (koelHoog || km2 >= 45) {
        out.push({
            categorie: 'koeling',
            titel: 'Actieve koeling',
            toelichting: 'Koellast of zonlast op glas is fors: overweeg actieve koeling (split, kanaal of waterzijdig) naast zonwering en HR glas.',
        });
    }
    else {
        out.push({
            categorie: 'koeling',
            titel: 'Passieve koeling eerst',
            toelichting: 'Beperkte koelpiek: zonwering, nachtventilatie en goede glaskeuze lossen vaak veel op; actieve koeling optioneel.',
        });
    }
    if (isAppartementachtigGebouw(input.type_woning)) {
        out.push({
            categorie: 'overig',
            titel: 'Appartement',
            toelichting: 'Controleer VvE-regels voor buitenunit, leidingroutes en geluid. Eventueel waterzijdige oplossing of gedeelde installatieruimte.',
        });
    }
    out.push({
        categorie: 'ventilatie',
        titel: 'Ventilatie en warmteterugwinning',
        toelichting: 'In deze scan zit ventilatie als “hoe vaak de lucht in huis wisselt”. Een systeem dat warmte uit de afvoerlucht terugwint, helpt in de praktijk — dat rekenen we hier niet apart uit; laat dat door je installateur koppelen aan de geldende bouwregels.',
    });
    out.push({
        categorie: 'combinatie',
        titel: 'Zonne-energie + warmtepomp',
        toelichting: 'PV helpt de elektravraag van de WP; dimensioneer PV los van thermische last maar neem piekvermogen netaansluiting mee in offerte.',
    });
    out.push({
        categorie: 'combinatie',
        titel: 'Warmtenet / stadswarmte',
        toelichting: 'Bij nieuwbouw of cluster: alternatief voor individuele WP; vraag aansluitvoorwaarden en jaarlijkse variabele kosten na.',
    });
    return out;
}
function computeGebouwIntensiteit(input, warmteW, koelW) {
    const opp = Math.max(input.vloeroppervlakte, 0.0001);
    const vol = Math.max(input.vloeroppervlakte * input.plafondhoogte, 0.0001);
    return {
        warmte_W_m2: Math.round(warmteW / opp),
        koel_W_m2: Math.round(koelW / opp),
        volume_m3: Math.round(vol * 10) / 10,
        warmte_W_m3: Math.round(warmteW / vol),
        koel_W_m3: Math.round(koelW / vol),
    };
}
export function computeComfortEngine(input) {
    const geoms = resolveRoomGeometries(input);
    const pessimistic = computeScenarioFromRooms(input, geoms, { u: 1.14, n: 1.12, sol: 1.12 });
    const baseR = computeScenarioFromRooms(input, geoms, { u: 1, n: 1, sol: 1 });
    const optimistic = computeScenarioFromRooms(input, geoms, { u: 0.88, n: 0.9, sol: 0.88 });
    const wKw = roundKW(baseR.warmteW);
    const kKw = roundKW(baseR.koelW);
    const piek = Math.max(wKw, kKw);
    const scenarios = [
        {
            id: 'pessimistic',
            label: 'Conservatief',
            subtitle: 'Slechtere schil / meer luchtverlies / meer zon',
            warmtelastKW: roundKW(pessimistic.warmteW),
            koellastKW: roundKW(pessimistic.koelW),
            pieklastKW: Math.max(roundKW(pessimistic.warmteW), roundKW(pessimistic.koelW)),
        },
        {
            id: 'base',
            label: 'Referentie',
            subtitle: 'Typische invoer — gebruik voor offertes',
            warmtelastKW: wKw,
            koellastKW: kKw,
            pieklastKW: piek,
        },
        {
            id: 'optimistic',
            label: 'Gunstig',
            subtitle: 'Strakkere schil / minder verlies / minder zon',
            warmtelastKW: roundKW(optimistic.warmteW),
            koellastKW: roundKW(optimistic.koelW),
            pieklastKW: Math.max(roundKW(optimistic.warmteW), roundKW(optimistic.koelW)),
        },
    ];
    const jaarVerwarm = Math.round(wKw * 1650);
    const jaarKoel = Math.round(kKw * 380);
    const adv = systemAdvice(piek);
    const copMin = 2.8;
    const copMax = 4.2;
    const jaarElekMin = Math.round((jaarVerwarm + jaarKoel * 0.35) / copMax);
    const jaarElekMax = Math.round((jaarVerwarm + jaarKoel * 0.35) / copMin);
    const engineInsights = buildEngineInsights(input, baseR.core, scenarios, jaarVerwarm, jaarKoel, jaarElekMin, jaarElekMax);
    const ruimteLasten = finalizeRoomJaar(baseR.rowsDraft, jaarVerwarm, jaarKoel, baseR.warmteW, baseR.koelW);
    const jipEnJannekeSamenvatting = jipEnJannekeBureauSamenvatting(input.ruimtes.length);
    const gebouwIntensiteit = computeGebouwIntensiteit(input, baseR.warmteW, baseR.koelW);
    const maxKamerWm2 = ruimteLasten.length
        ? Math.max(...ruimteLasten.map((r) => r.warmte_W_m2))
        : 0;
    const koelHoog = kKw >= Math.max(wKw * 0.55, 2.2);
    const segmentRegelgevingKort = segmentRegelgevingKortText(input.user_segment);
    const adviesTechnieken = [
        ...buildOfficieelAdviesRegels(input),
        ...buildSituatieInstallatieAdvies(input, piek),
        ...buildWensenAdvies(input, piek, maxKamerWm2, koelHoog),
        ...buildTechniekAdvies(input, piek, gebouwIntensiteit.warmte_W_m2, gebouwIntensiteit.koel_W_m2, maxKamerWm2, koelHoog),
    ];
    return {
        warmtelastKW: wKw,
        koellastKW: kKw,
        pieklastKW: piek,
        pieklastKWDisplay: piek,
        jaarverbruik: jaarVerwarm + jaarKoel,
        jaarverbruikWarmteKWh: jaarVerwarm,
        jaarverbruikKoelKWh: jaarKoel,
        userSegment: input.user_segment,
        engineInsights,
        systeem: adv.systeem,
        alternatief: adv.alternatief,
        engineVersion: COMFORT_ENGINE_VERSION,
        assumptionSetId: ASSUMPTION_SET_ID,
        methodologyNote: METHODOLOGY_NOTE,
        disclaimer: DISCLAIMER,
        designConditions: {
            tIndoorC: input.temperatuur,
            tOutdoorWinterC: -10,
            tOutdoorSummerC: 28,
            note: 'NL-ontwerpproxy; geen uurlijkse simulatie.',
        },
        scenarios,
        primaryScenarioId: 'base',
        lineItemsBase: lineItems(baseR.core),
        jaarverbruikElektraHypotheseKWh: {
            min: jaarElekMin,
            max: jaarElekMax,
            note: `Indicatief elektra warmtepomp bij COP ${copMin}–${copMax}; werkelijk verbruik hangt af van gedrag, afgifte en systeemkeuze.`,
        },
        ruimteLasten,
        jipEnJannekeSamenvatting,
        gebouwIntensiteit,
        adviesTechnieken,
        segmentRegelgevingKort,
    };
}
export function computeComfortAdvice(input) {
    return computeComfortEngine(input);
}
/** Pas jaartotalen aan op gemeten graaddagen (Open‑Meteo archief). Piek-kW ongewijzigd. */
export function applyClimateYearlyToResult(result, payload, input) {
    const refHdd = NL_INTERIOR_DEGREE_DAY_REF.hdd18;
    const refCdd = NL_INTERIOR_DEGREE_DAY_REF.cdd24;
    const factorHeat = refHdd > 0 ? payload.hdd18 / refHdd : 1;
    const factorCool = refCdd > 0 ? Math.max(0.55, payload.cdd24 / refCdd) : 1;
    const wKw = result.warmtelastKW;
    const kKw = result.koellastKW;
    const jaarVerwarm = Math.round(wKw * 1650 * factorHeat);
    const jaarKoel = Math.round(kKw * 380 * factorCool);
    const copMin = 2.8;
    const copMax = 4.2;
    const jaarElekMin = Math.round((jaarVerwarm + jaarKoel * 0.35) / copMax);
    const jaarElekMax = Math.round((jaarVerwarm + jaarKoel * 0.35) / copMin);
    const prevWarm = result.jaarverbruikWarmteKWh;
    const prevKoel = result.jaarverbruikKoelKWh;
    const fW = prevWarm > 0 ? jaarVerwarm / prevWarm : 1;
    const fK = prevKoel > 0 ? jaarKoel / prevKoel : 1;
    const ruimteLastenScaled = result.ruimteLasten.map((row) => ({
        ...row,
        jaar_warmte_kWh: Math.round(row.jaar_warmte_kWh * fW),
        jaar_koel_kWh: Math.round(row.jaar_koel_kWh * fK),
    }));
    const geomsC = resolveRoomGeometries(input);
    const baseRoom = computeScenarioFromRooms(input, geomsC, { u: 1, n: 1, sol: 1 });
    const baseCore = baseRoom.core;
    const climateNote = 'Jaartotalen geschaald met historische graaddagen (Open‑Meteo, regio-proxy via postcode).';
    const engineInsights = buildEngineInsights(input, baseCore, result.scenarios, jaarVerwarm, jaarKoel, jaarElekMin, jaarElekMax, climateNote);
    const climateContext = {
        source: payload.source,
        attribution: payload.attribution,
        period: `${payload.startDate} → ${payload.endDate}`,
        locationLabel: payload.locationLabel,
        lat: payload.lat,
        lon: payload.lon,
        hdd18: payload.hdd18,
        cdd24: payload.cdd24,
        meanTempYear: payload.meanTempYear,
        refHdd,
        refCdd,
        factorHeat: Math.round(factorHeat * 1000) / 1000,
        factorCool: Math.round(factorCool * 1000) / 1000,
        note: payload.note,
    };
    return {
        ...result,
        jaarverbruik: jaarVerwarm + jaarKoel,
        jaarverbruikWarmteKWh: jaarVerwarm,
        jaarverbruikKoelKWh: jaarKoel,
        engineInsights,
        jaarverbruikElektraHypotheseKWh: {
            min: jaarElekMin,
            max: jaarElekMax,
            note: result.jaarverbruikElektraHypotheseKWh.note,
        },
        climateContext,
        ruimteLasten: ruimteLastenScaled,
    };
}
/** Zelfde geometrie en U-proxy’s als `computeComfortEngine`; levert UA voor winter/zomer ontwerp-ΔT. */
export function computeBuildingLinearThermalCoeffs(input) {
    const ni = normalizeComfortInput(input);
    const geom = resolveRoomGeometries(ni);
    const baseR = computeScenarioFromRooms(ni, geom, { u: 1, n: 1, sol: 1 });
    const T_i = ni.temperatuur;
    const dTHeat = Math.max(0.1, T_i - -10);
    const dTCool = Math.max(0.1, 28 - T_i);
    const c = baseR.core;
    const uaHeat = (c.qTransHeat + c.qVentHeat) / dTHeat;
    const uaCool = (c.qTransCool + c.qVentCool) / dTCool;
    const V = geom.reduce((s, g) => s + g.V_m3, 0);
    return {
        uaHeat_WperK: uaHeat,
        uaCool_WperK: uaCool,
        internalGains_W: c.qInt,
        volume_m3: V,
        tIndoorC: T_i,
        dTDesignWinter_K: dTHeat,
        dTDesignSummer_K: dTCool,
        designTOutdoorWinterC: -10,
        designTOutdoorSummerC: 28,
    };
}
const DEFAULT_KOEL_GRENS_C = 24;
function hourlyMetaFromZones(zoneCoeffs, inertia) {
    let cTot = 0;
    let uaTot = 0;
    for (const z of zoneCoeffs) {
        cTot += thermalCapacityWhPerKFromVolume(z.volume_m3, inertia);
        uaTot += envelopeUaWPerK(z.uaHeat_WperK, z.uaCool_WperK);
    }
    return {
        heatCapacity_WhPerK: Math.round(cTot),
        uaEnv_WperK: uaTot,
        thermalInertia: inertia,
    };
}
/**
 * Quasi-stationair + 1R1C — zelfde weerreeks.
 * Bij meerdere ruimten: ontkoppelde zone-lasten (verschil zon per façade), totalen opgeteld.
 * Optioneel `ghiWm2` (W/m² globaal horizontaal): zon op ramen via façade-proxy, zie `solarHourlyGains.ts`.
 */
export function runHourlyThermalFullBundle(input, temperatureC, options) {
    const ni = normalizeComfortInput(input);
    const ve = validateComfortInput(ni);
    if (ve.length)
        return { ok: false, errors: ve };
    const nH = temperatureC.length;
    const ghi = options?.ghiWm2;
    if (ghi && ghi.length !== nH) {
        return { ok: false, errors: ['ghiWm2 moet dezelfde lengte hebben als temperatureC.'] };
    }
    const extraRaw = options?.solarAndOtherGainsW;
    if (extraRaw && extraRaw.length !== nH) {
        return { ok: false, errors: ['solarAndOtherGainsW moet dezelfde lengte hebben als temperatureC.'] };
    }
    const coeffs = computeBuildingLinearThermalCoeffs(ni);
    const koel = options?.koelgrensc ?? DEFAULT_KOEL_GRENS_C;
    const inertia = options?.thermalInertia ?? 'normaal';
    const geom = resolveRoomGeometries(ni);
    const zoneCoeffs = computePerZoneHourlyLinearCoeffs(ni);
    const useUncoupled = zoneCoeffs.length > 1 && !options?.forceSingleZoneModel;
    const sumFloor = geom.reduce((s, g) => s + g.vloer_m2, 0) || 1;
    let solarByRoom = null;
    if (ghi && ghi.length && geom.length) {
        const props = buildRoomSolarPropsForHourly(ni, geom);
        solarByRoom = hourlySolarGainWPerRoomSeries(ghi, props);
    }
    const mergedTotal = new Array(nH).fill(0);
    for (let h = 0; h < nH; h++) {
        let s = 0;
        if (solarByRoom) {
            for (let i = 0; i < solarByRoom.length; i++)
                s += solarByRoom[i][h] ?? 0;
        }
        const ex = extraRaw && extraRaw.length > h ? extraRaw[h] : 0;
        mergedTotal[h] = s + ex;
    }
    const hasMergedGains = mergedTotal.some((v) => Math.abs(v) > 1e-9);
    const zoneSolar = zoneCoeffs.map((_, zi) => {
        const row = new Array(nH).fill(0);
        for (let h = 0; h < nH; h++) {
            const fromSun = solarByRoom && solarByRoom[zi] ? solarByRoom[zi][h] : 0;
            const ex = extraRaw && extraRaw.length > h ? extraRaw[h] : 0;
            const share = (geom[zi]?.vloer_m2 ?? 0) / sumFloor;
            row[h] = fromSun + ex * share;
        }
        return row;
    });
    const exposeSolarSeries = Boolean(ghi?.length || (extraRaw && extraRaw.length));
    if (useUncoupled) {
        const zoneParamsQuasi = zoneCoeffs.map((zc, i) => ({
            uaHeat_WperK: zc.uaHeat_WperK,
            uaCool_WperK: zc.uaCool_WperK,
            internalGains_W: zc.internalGains_W,
            solarAndOtherGainsW: hasMergedGains ? zoneSolar[i] : undefined,
        }));
        const quasiSteady = simulateHourlyIdealLoadsUncoupledZones(temperatureC, zoneParamsQuasi, ni.temperatuur, koel);
        const zoneParamsRc = zoneCoeffs.map((zc, i) => ({
            uaHeat_WperK: zc.uaHeat_WperK,
            uaCool_WperK: zc.uaCool_WperK,
            internalGains_W: zc.internalGains_W,
            solarAndOtherGainsW: hasMergedGains ? zoneSolar[i] : undefined,
            heatCapacity_WhPerK: thermalCapacityWhPerKFromVolume(zc.volume_m3, inertia),
        }));
        const rc1Ideal = simulateHourly1R1CUncoupledZones(temperatureC, zoneParamsRc, ni.temperatuur, koel);
        const zones = zoneCoeffs.map((zc, i) => {
            const series = {
                temperatureC,
                solarAndOtherGainsW: hasMergedGains ? zoneSolar[i] : undefined,
            };
            const p = {
                uaHeat_WperK: zc.uaHeat_WperK,
                uaCool_WperK: zc.uaCool_WperK,
                internalGains_W: zc.internalGains_W,
                stookgrensC: ni.temperatuur,
                koelgrensc: koel,
            };
            const qZ = simulateHourlyIdealLoads(series, p);
            const uaE = envelopeUaWPerK(zc.uaHeat_WperK, zc.uaCool_WperK);
            const Cz = thermalCapacityWhPerKFromVolume(zc.volume_m3, inertia);
            const rZ = simulateHourly1R1CIdealLoads(series, p, Cz, uaE);
            return { id: zc.id, naam: zc.naam, coeffs: zc, quasiSteady: qZ, rc1Ideal: rZ };
        });
        return {
            ok: true,
            bundle: {
                quasiSteady,
                rc1Ideal,
                meta: hourlyMetaFromZones(zoneCoeffs, inertia),
                solarGainsTotal_W: exposeSolarSeries ? mergedTotal : undefined,
                zones,
                uncoupledMultiZone: true,
            },
            coeffs,
        };
    }
    const C = thermalCapacityWhPerKFromVolume(coeffs.volume_m3, inertia);
    const uaEnv = envelopeUaWPerK(coeffs.uaHeat_WperK, coeffs.uaCool_WperK);
    const series = {
        temperatureC,
        solarAndOtherGainsW: hasMergedGains ? mergedTotal : undefined,
    };
    const params = {
        uaHeat_WperK: coeffs.uaHeat_WperK,
        uaCool_WperK: coeffs.uaCool_WperK,
        internalGains_W: coeffs.internalGains_W,
        stookgrensC: coeffs.tIndoorC,
        koelgrensc: koel,
    };
    const quasiSteady = simulateHourlyIdealLoads(series, params);
    const rc1Ideal = simulateHourly1R1CIdealLoads(series, params, C, uaEnv);
    return {
        ok: true,
        bundle: {
            quasiSteady,
            rc1Ideal,
            meta: { heatCapacity_WhPerK: C, uaEnv_WperK: uaEnv, thermalInertia: inertia },
            solarGainsTotal_W: exposeSolarSeries ? mergedTotal : undefined,
            uncoupledMultiZone: false,
        },
        coeffs,
    };
}
export function runHourlyIdealLoadSimulation(input, temperatureC, options) {
    const ni = normalizeComfortInput(input);
    const ve = validateComfortInput(ni);
    if (ve.length)
        return { ok: false, errors: ve };
    const coeffs = computeBuildingLinearThermalCoeffs(ni);
    const series = {
        temperatureC,
        solarAndOtherGainsW: options?.solarAndOtherGainsW,
    };
    const hourly = simulateHourlyIdealLoads(series, {
        uaHeat_WperK: coeffs.uaHeat_WperK,
        uaCool_WperK: coeffs.uaCool_WperK,
        internalGains_W: coeffs.internalGains_W,
        stookgrensC: coeffs.tIndoorC,
        koelgrensc: options?.koelgrensc ?? DEFAULT_KOEL_GRENS_C,
    });
    return { ok: true, hourly, coeffs };
}
